<template>
  <div class="checkout-wrapper">
    <figure class="item bankid" v-if="!isVerified">
      <section class="bankid-select-login-method-container" v-if="!signProcedureStarted">
        <div class="thumbnail">
          <img src="../assets/img/icon_64x64@2x.png" alt="mobile_bankid" />
        </div>
        <figcaption class="caption first-bankid" id="mobilecaption">
          För att kunna <span v-if="!this.formState.isValidForPsychologicalTreatment">få recept</span
          ><span v-else>boka psykologisk behandling</span> behöver du identifiera dig med BankID.
        </figcaption>

        <div class="bankid-btn mobile valid" v-if="!isLoading" @click.stop="activateSignWithMobileBankId">
          Mobilt BankID
        </div>

        <div class="bankid-btn file" v-bind:class="{ valid: isDesktop }" @click.stop="activateSignWithBankIdOnFile">
          BankID
        </div>
      </section>

      <section class="mobile-bankid-container" v-if="signProcedureStarted && signWithMobileBankId">
        <figcaption class="caption" v-if="!isLoading" id="mobilecaption">
          <strong>Obs!</strong> Du behöver en mobiltelefon eller surfplatta med BankID säkerhetsapp installerad för att
          logga in med Mobilt BankID.
        </figcaption>

        <div
          class="bankid-btn mobile same-device valid"
          v-if="!isLoading && !signWithMobileBankIdOtherDevice && !isDesktop"
          @click.stop="makeBankIdV6SignCall"
        >
          BankID på samma enhet
        </div>

        <div
          class="bankid-btn other-device valid"
          v-bind:class="{ file: !isDesktop }"
          v-if="!isLoading && !signWithMobileBankIdOtherDevice"
          @click.stop="activateSignWithMobileBankIdOtherDevice"
        >
          BankID på annan enhet
        </div>

        <div v-if="shouldDisplayQRCode">
          <qrcode :value="qrCode" :options="{ width: 200 }" />
        </div>

        <div class="bankid-back-link" v-if="!isLoading" @click.stop="resetMobileBankIdState">&laquo; Tillbaka</div>
      </section>

      <section>
        <span class="bankid-feedback" v-if="isLoading && !errorMsg">
          <h5>Inloggning startar</h5>
          <p v-if="!bankidAppUrl && !bankidAppUrlFile">
            Öppna appen för Mobilt BankID på den enhet där du har den installerad.
          </p>
          <p v-if="signWithMobileBankIdOtherDevice">
            Scanna därefter QR-koden ovan.
          </p>
        </span>

        <div class="spinner-wrapper" v-if="isLoading">
          <vue-simple-spinner v-if="isLoading" line-fg-color="#333333" />
        </div>

        <!-- LAUNCH APP, FOR IOS ONLY -->
        <a class="bankid-btn ios-only valid" v-if="isLoading && displayiOSOpenButton" :href="`${bankidAppUrl}`">
          Öppna BankID-appen</a
        >

        <!-- LAUNCH APP FOR BANKID ON FILE -->
        <a class="bankid-btn desktop-only valid" v-if="isLoading && bankidAppUrlFile" :href="`${bankidAppUrlFile}`">
          Öppna BankID säkerhetsapp</a
        >

        <div class="bankid-btn cancel" v-if="isLoading" @click.stop="cancelMobileBankId">Avbryt</div>

        <span class="bankid-error" v-if="errorMsg">
          <p>{{ errorMsg }}</p>
        </span>
      </section>
    </figure>

    <figure class="item underage" v-if="isVerified && isUnderage">
      <Underage />
    </figure>

    <!-- PILLS SELECT -->
    <figure
      class="item pill-select"
      v-if="isVerified && !hasChosenPillAmount && !isUnderage && this.getConfirmationType === 'recipe'"
    >
      <CheckoutPills />
      <div class="form-error-message">
        <button @click="loadNextItem(1)" class="form-button">
          Nästa <span class="icon"><Zondicon icon="ArrowThinRight"/></span>
        </button>
      </div>
    </figure>

    <!-- CONSENT -->
    <figure
      class="item consent"
      v-if="
        isVerified && hasChosenPillAmount && !hasGivenConsent && !isUnderage && this.getConfirmationType === 'recipe'
      "
    >
      <PatientAlliance />
      <div class="form-error-message">
        <p class="kaddio-error-msg">{{ kaddioErrorMsg }}</p>
        <div class="confirm-button valid" @click="loadNextItem(2)">Jag samtycker</div>
        <!-- <div class="confirm-button valid" @click="tempKaddioErrorMsg()">Jag samtycker</div> -->
      </div>
    </figure>

    <!-- KLARNA -->
    <figure
      class="item klarna"
      v-if="
        isVerified &&
          hasChosenPillAmount &&
          hasGivenConsent &&
          !hasPayed &&
          !isUnderage &&
          this.getConfirmationType === 'recipe'
      "
    >
      <KlarnaCheckout :id-number="this.verifiedPersonalNumber" />
    </figure>

    <!-- BEKRÄFTELSE RECEPT -->
    <figure
      class="item contact-info"
      v-if="
        isVerified &&
          hasChosenPillAmount &&
          hasGivenConsent &&
          hasPayed &&
          hasAddedContactInfo &&
          !isUnderage &&
          this.getConfirmationType === 'recipe'
      "
    >
      <Confirmation />
    </figure>

    <!-- KONTAKTUPPGIFTER PSYKOLOG -->
    <!-- <figure class="item contact-info" v-if="!isVerified"> -->
    <figure
      class="item contact-info"
      v-if="isVerified && !isUnderage && !this.hasAddedEmailAndPhone && this.getConfirmationType === 'psychologist'"
    >
      <h2>Boka behandling</h2>
      Fyll i dina kontaktuppgifter och önskad kontaktväg. Vega Psykologi återkopplar inom kort med erbjudande om tid för
      samtal.

      <div class="contact-info-wrapper">
        <div class="name">
          <label>Namn</label>
          {{ name }}
        </div>
        <div class="input-container email">
          <label for="email">E-post</label>
          <input v-validate="{ required: true }" type="email" id="email" name="email" placeholder="" v-model="email" />
        </div>
        <div class="input-container phone">
          <label for="phone">Tel.nr</label>
          <input
            v-validate="{ required: true, min: 8, max: 16 }"
            type="phone"
            id="phone"
            name="phone"
            placeholder=""
            v-model="phone"
          />
        </div>
        <div class="input-container select-contact">
          <label for="preferredContact">Kontakta via</label>
          <select id="preferredContact" name="preferredContact" placeholder="" v-model="preferredContactMethod">
            <option value="e-post">E-post</option>
            <option value="telefon">Telefon</option>
          </select>
        </div>
      </div>

      <div
        class="confirm-button psychology"
        v-bind:class="{ valid: contactFieldsAreValid }"
        tabindex="0"
        @click.stop="loadNextItem(5)"
      >
        Skicka förfrågan
      </div>
    </figure>

    <!-- BEKRÄFTELSE PYSKOLOG -->
    <figure
      class="item contact-info"
      v-if="isVerified && !isUnderage && this.hasAddedEmailAndPhone && this.getConfirmationType === 'psychologist'"
    >
      <Confirmation />
    </figure>
  </div>
</template>
<script>
import axios from "axios"
import VueSimpleSpinner from "vue-simple-spinner"
import CheckoutPills from "../components/CheckoutComponents/CheckoutPills"
import PatientAlliance from "../components/CheckoutComponents/PatientAlliance"
import KlarnaCheckout from "../components/CheckoutComponents/KlarnaCheckout"
import Confirmation from "../components/CheckoutComponents/Confirmation"
import Underage from "../components/CheckoutComponents/Underage"

import { mapGetters } from "vuex"

export default {
  name: "checkout",
  inject: ["formState"],
  components: {
    VueSimpleSpinner,
    CheckoutPills,
    PatientAlliance,
    KlarnaCheckout,
    Confirmation,
    Underage
  },
  data() {
    return {
      personalNumber: "",
      resultText: "",
      verifiedPersonalNumber: "",
      isLoading: false,
      verificationData: {},
      name: "",
      firstname: "",
      lastname: "",
      isVerified: false,
      isUnderage: false,
      hasChosenPillAmount: false,
      hasGivenConsent: false,
      hasRequestedPsychologist: false,
      hasPayed: false,
      hasAddedContactInfo: false,
      hasAddedEmailAndPhone: false,
      email: "",
      phone: "",
      preferredContactMethod: "e-post",
      bankidResponse: "",
      errorMsg: "",
      kaddioErrorMsg: "",
      bankidAppUrl: "",
      bankidAppUrlFile: "",
      orderReference: "",
      signProcedureStarted: false,
      signWithMobileBankId: false,
      signWithMobileBankIdOtherDevice: false,
      signWithBankIdOnFile: false,
      displayiOSOpenButton: false,
      bankIdSignType: "",
      prevCompletionData: "",
      qrCode: "",
      displayQRCode: false,
      isBankIDAuthenticated: false
    }
  },
  methods: {
    activateSignWithMobileBankId() {
      this.signProcedureStarted = true
      this.signWithMobileBankId = true
      this.bankIdSignType = "mobile"
    },
    activateSignWithBankIdOnFile() {
      if (this.isDesktop) {
        this.signProcedureStarted = true
        this.signWithBankIdOnFile = true
        this.bankIdSignType = "file"
        this.makeBankIdV6SignCall()
      }
    },
    activateSignWithMobileBankIdOtherDevice() {
      this.signWithMobileBankIdOtherDevice = true
      this.displayQRCode = true
      this.makeBankIdV6SignCall()
    },
    resetMobileBankIdState() {
      this.signProcedureStarted = false
      this.signWithMobileBankId = false
      this.signWithBankIdOnFile = false
      this.bankidAppUrl = ""
      this.bankidAppUrlFile = ""
      this.signWithMobileBankIdOtherDevice = false
      this.bankIdSignType = ""
      this.displayQRCode = false
      this.qrCode = ""
      this.isBankIDAuthenticated = false

      setTimeout(() => {
        this.errorMsg = ""
      }, 8000)
    },
    makeBankIdV6SignCall() {
      this.errorMsg = ""
      this.isLoading = true

      // eslint-disable-next-line
      console.log("calling BankID v6")

      axios({
        method: "get",
        url: process.env.VUE_APP_API_BASE_URL + "bankid/v6sign",
        params: {
          type: this.bankIdSignType
        }
      }).then(signRes => {
        this.bankidResponse = signRes

        if (!signRes.data.orderRef) {
          this.errorMsg = "Signeringen avbröts. Försök igen."
          this.isLoading = false
          this.isVerified = false
          this.resetMobileBankIdState()
          return
        }

        this.orderReference = signRes.data.orderRef

        if (this.signWithMobileBankIdOtherDevice) {
          // Display QR Code and start polling for status updates
          this.qrCode = signRes.data.qrCode
          this.pollForQRStatus(this.orderReference)
        } else {
          this.bankidAppUrl = signRes.data.url_desktop

          if (this.$device.windows && this.bankIdSignType === "file") {
            this.bankidAppUrlFile = signRes.data.url_desktop
            window.open(signRes.data.url_desktop)
          }

          if (this.bankIdSignType === "mobile") {
            if (this.$device.android || this.$device.androidPhone) {
              window.open(signRes.data.url_desktop, "_blank")
            }

            if (this.$device.ios) {
              this.bankidAppUrl = signRes.data.url_mobile
              this.displayiOSOpenButton = true
            }
          }
        }

        this.makeBankIdV6CollectCall(this.orderReference)
      })
    },
    makeBankIdV6CollectCall(orderRef) {
      axios({
        method: "get",
        url: process.env.VUE_APP_API_BASE_URL + "bankid/v6collect",
        params: {
          orderRef: orderRef
        }
      })
        .then(collectRes => {
          let result = collectRes.data
          if (result.status === "completed") {
            this.isBankIDAuthenticated = true
            this.displayQRCode = false
            this.signWithMobileBankIdOtherDevice = false

            this.name = result.details.user.name
            this.firstname = result.details.user.givenName
            this.lastname = result.details.user.surname
            this.verifiedPersonalNumber = result.details.user.personalNumber

            this.$store.commit("persist/setIdentificationNumber", {
              identificationNumber: result.details.user.personalNumber
            })
            this.$store.commit("persist/setName", {
              givenName: result.details.user.givenName,
              familyName: result.details.user.surname
            })

            this.$store.commit("persist/setVerificationData", {
              cert: "",
              signature: result.details.signature,
              ocspResponse: result.details.ocspResponse
            })

            this.isLoading = false
            this.isVerified = true

            pushCheckoutEventToDataLayer("bankid-complete")
          } else {
            this.makeBankIdV6CollectCall(orderRef)
          }
          return true
        })
        .catch(() => {
          //eslint-disable-next-line
          this.errorMsg = "Signeringen avbröts. Försök igen."
          this.isLoading = false
          this.isVerified = false
          this.resetMobileBankIdState()
        })
    },
    pollForQRStatus(orderRef) {
      let timeoutInSeconds = 26
      const intervalId = setInterval(() => {
        if (this.isBankIDAuthenticated) {
          // Check if authentication is already completed
          clearInterval(intervalId)
          return
        }

        axios({
          method: "get",
          url: `${process.env.VUE_APP_API_BASE_URL}bankid/v6sign/status/${orderRef}`
        })
          .then(response => {
            const { qrCode, status } = response.data

            // Update the QR code in the UI
            this.qrCode = qrCode

            // Check the status of the authentication
            if (status !== "pending") {
              // If the status is not pending, stop polling and handle the result
              clearInterval(intervalId)
              if (status === "success") {
                // Handle success (e.g., navigate to a different page or show a success message)
              } else {
                // Handle error or failure (e.g., show an error message)
                this.errorMsg = "Signeringen avbröts. Försök igen."
                this.isLoading = false
                this.isVerified = false
                this.resetMobileBankIdState()
              }
            }
          })
          .catch(error => {
            //eslint-disable-next-line
            console.error("Error polling for status:", error)
            clearInterval(intervalId) // Clear the interval timer
            clearTimeout(timeoutId) // Clear the timeout timer
            this.errorMsg = "Signeringen avbröts. Försök igen."
            this.isLoading = false
            this.isVerified = false
            this.resetMobileBankIdState()
          })
      }, 2000) // Poll every 2 seconds

      const timeoutId = setTimeout(() => {
        if (!this.isBankIDAuthenticated) {
          clearInterval(intervalId)
          this.errorMsg = "Signeringen avbröts. Försök igen."
          this.cancelMobileBankId()
        }
      }, timeoutInSeconds * 1000)
    },
    cancelMobileBankId() {
      if (this.orderReference) {
        axios({
          method: "get",
          url: process.env.VUE_APP_API_BASE_URL + "bankid/v6cancel",
          params: {
            orderRef: this.orderReference
          }
        }).then(() => {
          this.isLoading = false
          this.isVerified = false
          this.resetMobileBankIdState()
          return true
        })
      } else {
        this.isLoading = false
        this.isVerified = false
        this.resetMobileBankIdState()
        return true
      }
    },
    loadNextItem(val) {
      if (val === 1) {
        this.hasChosenPillAmount = true
        pushCheckoutEventToDataLayer("product-selected-complete")
      }

      if (val === 2) {
        // eslint-disable-next-line
        console.log("sending pre data")
        this.sendKaddioPreData()
      }

      if (val === 3) {
        this.hasPayed = true
      }

      if (val === 4) {
        this.hasAddedContactInfo = true
      }

      // Send psychologist request
      if (val === 5) {
        if (this.contactFieldsAreValid) {
          this.$store.commit("persist/setShowConfirmation", { showConfirmation: true })
          this.$store.commit("persist/setPhone", { phone: this.phone })
          this.$store.commit("persist/setEmail", { email: this.email })
          this.$store.commit("persist/setPreferredContact", { preferredContact: this.preferredContactMethod })

          this.hasAddedEmailAndPhone = true
        }
      }
    },
    tempKaddioErrorMsg() {
      this.kaddioErrorMsg =
        "Vi upplever för närvarande tekniska problem och har därför tillfälligt stängt av möjligheten att skicka receptförfrågningar. Välkommen att försöka igen senare."
    },
    checkUnderage(val) {
      const yyyy = val.substring(0, 4)
      const mm = val.substring(4, 6)
      const dd = val.substring(6, 8)
      const dateString = yyyy + "-" + mm + "-" + dd

      const today = new Date()

      const birthDate = new Date(dateString)

      let age = today.getFullYear() - birthDate.getFullYear()
      const month = today.getMonth() - birthDate.getMonth()
      if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }

      const isUnderage = age < 18 ? true : false

      return isUnderage
    },
    sendKaddioPreData() {
      const kaddioData = {
        formId: process.env.VUE_APP_KADDIO_PRE_FORM_ID,
        pnr: this.getIdNumber,
        answers: [
          [
            this.getFinalFormData // KADDIO: form_data
          ]
        ],
        firstname: this.firstname,
        lastname: this.lastname,
        email: ""
      }

      // prevent posting form twice
      if (this.getIdNumber !== "") {
        axios({
          method: "post",
          url: process.env.VUE_APP_API_BASE_URL + "sendtokaddio",
          data: kaddioData,
          headers: {
            "Content-Type": "application/json"
          }
        })
          .then(response => {
            // eslint-disable-next-line
            console.log("Kaddio pre-data sent: " + response)

            // Move on to Klarna
            this.kaddioErrorMsg = ""
            this.hasGivenConsent = true

            pushCheckoutEventToDataLayer("patient-alliance-complete")

            return true
          })
          .catch(response => {
            // eslint-disable-next-line
            console.log("Kaddio data could not be posted: " + response)
            this.kaddioErrorMsg =
              "Ajdå, det verkar som att vi har problem att etablera en koppling till vårt journalsystem för tillfället. Ladda om sidan och försök igen. Om problemet kvarstår, kontakta oss på info@potenshjalpen.se."
            return false
          })
      }
    }
  },
  computed: {
    ...mapGetters("persist", [
      "getConfirmationType",
      "getIdentificationNumber",
      "getGivenName",
      "getFamilyName",
      "getCert",
      "getSignature",
      "getOcspResponse",
      "getFinalFormData",
      "getIdNumber"
    ]),
    personalNumberFormatted() {
      return this.personalNumber.replace(/-|\s/g, "")
    },
    personalNumberIsValid() {
      if (this.fields.personalNumber) {
        return this.fields.personalNumber.valid
      } else {
        return false
      }
    },
    phoneIsValid() {
      if (this.fields.phone) {
        return this.fields.phone.valid
      } else {
        return false
      }
    },
    emailIsValid() {
      if (this.fields.email) {
        return this.fields.email.valid
      } else {
        return false
      }
    },
    contactFieldsAreValid() {
      if (this.fields.email && this.fields.phone) {
        return this.fields.email.valid && this.fields.phone.valid
      } else {
        return false
      }
    },
    isDesktop() {
      if (this.$device.ios || this.$device.android) {
        return false
      } else {
        return true
      }
    },
    otherDeviceButtonText() {
      if (this.$device.ios) {
        return "Signera med mobilt BankID"
      } else {
        return "BankID på annan enhet"
      }
    },
    shouldDisplayQRCode() {
      return this.displayQRCode && this.qrCode && this.qrCode.length > 0
    }
  }
}

function pushCheckoutEventToDataLayer(eventName) {
  window.dataLayer.push({
    event: eventName
  })
}
</script>
